<template>
  <GlassCard class="duel-card" padding="none" radius="xl" :highlight="highlight">
    <div class="duel-card__inner" :class="{ 'duel-card__inner--compact': compact }">
      <CornerFrame fill class="duel-card__photo" :radius="18" :corner-size="compact ? 26 : 34" :inset="compact ? 12 : 18">
        <img
          v-if="photoUrl && !failed"
          :src="photoUrl"
          :alt="displayName"
          class="duel-card__img"
          :class="{ 'duel-card__img--mirror': mirror }"
          draggable="false"
          @error="failed = true"
        />
        <div v-else class="duel-card__placeholder" :style="{ background: gradient }">
          <span v-if="player" class="duel-card__initials">{{ initials }}</span>
          <span v-else class="duel-card__skeleton cb-skeleton"></span>
        </div>
        <template #overlay>
          <span v-if="tag" class="duel-card__tag" :class="{ 'duel-card__tag--lime': tagLime }">{{ tag }}</span>
        </template>
      </CornerFrame>
      <div class="duel-card__name cb-ellipsis">
        <span v-if="player">{{ displayName }}</span>
        <span v-else class="duel-card__name-skeleton cb-skeleton"></span>
      </div>
      <PlayerStats
        class="duel-card__stats"
        icons
        :size="compact ? 'md' : 'lg'"
        :wins="player ? player.countWins : 0"
        :losses="player ? player.countLosses : 0"
      />
    </div>
  </GlassCard>
</template>

<script setup>
/**
 * DuelPlayerCard — карточка участника дуэли (экраны 4 и 6): фото/аватар в лаймовых уголках,
 * имя, ★ победы / ☆ поражения.
 *
 * <DuelPlayerCard :player="game.opponent" />
 * <DuelPlayerCard :player="me" :photo="looserImgLink" tag="Проиграл" />
 *
 * props: player (нормализованный PlayerInfo | null → скелетон), photo (url вместо avatarUrl),
 *        mirror (зеркалить фото), tag (подпись в углу фото), tagLime, highlight, compact.
 * Высоту задаёт родитель (flex): фото растягивается, имя и статистика — по содержимому.
 */
import { computed, ref, watch } from "vue";
import GlassCard from "@/components/ui/GlassCard.vue";
import CornerFrame from "@/components/ui/CornerFrame.vue";
import PlayerStats from "@/components/ui/PlayerStats.vue";
import { avatarGradient, initialsOf } from "@/utils/player";

const props = defineProps({
  player: { type: Object, default: null },
  photo: { type: String, default: null },
  mirror: { type: Boolean, default: false },
  tag: { type: String, default: "" },
  tagLime: { type: Boolean, default: false },
  highlight: { type: Boolean, default: false },
  compact: { type: Boolean, default: false },
});

const failed = ref(false);
const photoUrl = computed(() => props.photo || (props.player && props.player.avatarUrl) || null);
watch(photoUrl, () => {
  failed.value = false;
});

const displayName = computed(() => (props.player ? props.player.playerName : ""));
const initials = computed(() => initialsOf(displayName.value));
const gradient = computed(() => avatarGradient(displayName.value || "?"));
</script>

<style scoped>
.duel-card {
  height: 100%;
  min-height: 0;
}

.duel-card__inner {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  padding: 14px 14px 12px;
  gap: 10px;
}

.duel-card__inner--compact {
  padding: 10px 10px 8px;
  gap: 6px;
}

.duel-card__photo {
  flex: 1 1 auto;
  min-height: 72px;
}

.duel-card__img {
  object-position: center 30%;
  -webkit-user-drag: none;
}

.duel-card__img--mirror {
  transform: scaleX(-1);
}

.duel-card__placeholder {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.duel-card__placeholder::after {
  content: "";
  position: absolute;
  inset: 0;
  background: radial-gradient(70% 90% at 50% 40%, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.45) 100%);
}

.duel-card__initials {
  position: relative;
  z-index: 1;
  font-size: clamp(40px, 12vw, 64px);
  font-weight: 800;
  letter-spacing: 0.02em;
  color: rgba(255, 255, 255, 0.92);
  text-shadow: 0 2px 18px rgba(0, 0, 0, 0.45);
}

.duel-card__skeleton {
  position: absolute;
  inset: 0;
  border-radius: 0;
}

.duel-card__tag {
  position: absolute;
  top: 10px;
  left: 50%;
  z-index: 3;
  transform: translateX(-50%);
  padding: 4px 10px;
  border-radius: var(--cb-radius-pill);
  background: rgba(11, 14, 10, 0.72);
  border: 1px solid var(--cb-border-strong);
  color: var(--cb-text);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
  -webkit-backdrop-filter: blur(6px);
  backdrop-filter: blur(6px);
}

.duel-card__tag--lime {
  border-color: var(--cb-lime-border);
  color: var(--cb-lime);
}

.duel-card__name {
  flex-shrink: 0;
  padding: 0 4px;
  font-size: 26px;
  font-weight: 700;
  line-height: 1.15;
  letter-spacing: -0.01em;
}

.duel-card__inner--compact .duel-card__name {
  font-size: 20px;
}

.duel-card__name-skeleton {
  display: block;
  width: 55%;
  height: 26px;
}

.duel-card__stats {
  flex-shrink: 0;
  padding: 0 6px 2px;
}

@media (max-height: 700px) {
  .duel-card__name {
    font-size: 21px;
  }
}
</style>
